import { Box, Container } from "@mui/material";
import { useFetchDetail, usePutDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import dayjs from "dayjs";
import { useAddEditVirtualEdition } from "../hooks/useAddEditVirtualEdition";
import AnimationFadeIn from "@components/AnimationFadeIn";
import EditionFormCst from "@components/EditionFormCst";
import { useEffect, useMemo, useState } from "react";
import { useGetEnrollmentStatus } from "@hooks/useGetEnrollmentStatus";

const EditVirtualEditions: React.FC = () => {
  const { t } = useTranslation(["virtual-editions-page"]);
  const { idEdition, id } = useParams();
  const [activityDuration, setActivityDuration] = useState(0);
  const [showModalWarning, setShowModalWarning] = useState(false);
  const {
    columns,
    dataDetail,
    actionsForForms,
    mutate,
    isLoading: isLoadingDetail,
  } = useFetchDetail(
    ServicesURL.getEditionsDetail + `/virtual`,
    idEdition + `?corporateId=${id}` || "",
    PagesID["corporate.virtual-editions"],
  );

  const { dataDetail: sessionDetail, isLoading: isLoadingSession } =
    useFetchDetail(
      ServicesURL.getEditions + `/virtualSessionsFlat`,
      idEdition + `?corporateId=${id}` || "",
      PagesID["corporate.virtual-editions"],
    );

  const navigate = useNavigate();

  const { trigger } = usePutDetail(
    ServicesURL.getEditions + `/virtual/${idEdition}?corporateId=${id}`,
  );

  const { columns: sessionColumns } = useFetchDetail(
    ServicesURL.getEditions,
    undefined,
    PagesID["corporate.virtual-editions.add"],
  );

  const hasMeetingTool = useMemo(() => {
    if (!sessionDetail || !Array.isArray(sessionDetail.sessions)) return false;

    return sessionDetail.sessions.some((session: { tool?: string }) =>
      ["MSTEAMS", "GMEET"].includes((session?.tool || "").toUpperCase()),
    );
  }, [sessionDetail]);

  const { enrollmentStatus } = useGetEnrollmentStatus(
    idEdition,
    hasMeetingTool,
  );

  const confirmedDisabledTooltip = useMemo(() => {
    if (
      hasMeetingTool &&
      enrollmentStatus &&
      !enrollmentStatus.allEnrollmentsCompleted
    )
      return t("enrollment-warning.meeting-disabled");

    return undefined;
  }, [hasMeetingTool, enrollmentStatus, t]);

  const {
    onSubmit,
    setOpenConfirmStatus,
    openConfirmStatus,
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    getDisabled,
    openAssociations,
    facultiesAssociation,
    setFacultiesAssociation,
    tutorsAssociation,
    producerAssociation,
    presenterAssociation,
    coorganizerAssociation,
    cohostAssociation,
    setProducerAssociation,
    setPresenterAssociation,
    setCoorganizerAssociation,
    setCohostAssociation,
    setTutorsAssociation,
    resetSessionAssociations,
    hasDifferentDuration,
    savedInputForm,
    savedSessions,
  } = useAddEditVirtualEdition(
    trigger,
    id,
    dataDetail,
    mutate,
    activityDuration,
  );

  const { dataDetail: activityDetail } = useFetchDetail(
    dataDetail.learning_objects?.id
      ? `${ServicesURL.getActivities.replace(":idCorporate", `${id}`)}`
      : "",
    dataDetail.learning_objects?.id || "",
    PagesID["catalog.activities"],
  );

  useEffect(() => {
    setActivityDuration(activityDetail?.duration);
  }, [activityDetail]);

  const inputsException = (col: any, inputForm: any) => {
    if (
      dataDetail &&
      Object.keys(dataDetail).length > 0 &&
      columns?.length > 0 &&
      dayjs().isAfter(dayjs(dataDetail["publish_date"]))
    ) {
      if (
        !["edition_code", "faculties", "edition_name"].includes(col.accessorKey)
      ) {
        col.disabled = true;
      }
    } else {
      if (
        !["edition_code", "faculties", "edition_name"].includes(col.accessorKey)
      ) {
        col.disabled = false;
      }
    }

    return col;
  };

  useEffect(() => {
    if (hasDifferentDuration) {
      setShowModalWarning(true);
    }
  }, [hasDifferentDuration]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-editions-virtual.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("edit.hero-title")}
          description={t("edit.hero-description")}
        />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            {!isLoadingDetail && !isLoadingSession && (
              <EditionFormCst
                isVirtual={true}
                row={{ ...dataDetail, ...sessionDetail }}
                columns={columns}
                sessionColumns={sessionColumns}
                inputsException={inputsException}
                onSubmit={onSubmit}
                backButton={() => {
                  navigate(-1);
                }}
                setOpenAssociation={(accessorKey, open) =>
                  setOpenAssociation(accessorKey, open)
                }
                handleDelete={(accessorKey, el) =>
                  handleDelete(accessorKey, el)
                }
                handleAssociation={(accessorKey, value) =>
                  handleAssociation(accessorKey, value)
                }
                getValues={(accessorKey) => getValues(accessorKey)}
                getDisabled={(accessorKey) => getDisabled(accessorKey)}
                association={openAssociations}
                resetSessionAssociations={resetSessionAssociations}
                facultiesAssociation={facultiesAssociation}
                tutorsAssociation={tutorsAssociation}
                producerAssociation={producerAssociation}
                presenterAssociation={presenterAssociation}
                coorganizerAssociation={coorganizerAssociation}
                cohostAssociation={cohostAssociation}
                setProducerAssociation={setProducerAssociation}
                setPresenterAssociation={setPresenterAssociation}
                setCoorganizerAssociation={setCoorganizerAssociation}
                setCohostAssociation={setCohostAssociation}
                setFacultiesAssociation={setFacultiesAssociation}
                setTutorsAssociation={setTutorsAssociation}
                setActivityDuration={setActivityDuration}
                confirmedDisabledTooltip={confirmedDisabledTooltip}
              />
            )}
          </Box>
        )}
        <ModalConfirmsCst
          open={!!openConfirmStatus}
          title={t("outcome-item", { ns: "modal" })}
          description={t("outcome-item", { ns: "modal" })}
          onCallBackClosed={() => setOpenConfirmStatus(undefined)}
          status={openConfirmStatus}
        />
        <ModalConfirmsCst
          open={showModalWarning}
          title={t("duration-warning.title")}
          description={t("duration-warning.description")}
          routeToSamePage={true}
          onActionConfirmed={() => {
            setShowModalWarning(false);
            savedSessions && onSubmit(savedInputForm, savedSessions, true);
          }}
          onCallBackClosed={() => {
            setShowModalWarning(false);
          }}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default EditVirtualEditions;
